import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { AVAILABLE_LANGUAGES, HTTP_STATUS } from '../constants/constants';
import { selectLanguage } from '../state/language/language.selectors';
import { TranslateService } from '@ngx-translate/core';
import { extractErrorMessage } from '../utils/extract-error-message.util';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor, OnDestroy {
  private selectedLanguage: string = AVAILABLE_LANGUAGES.ES;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private messageError: string = 'common.internalServerError';

  constructor(
    private readonly _toastrSvc: ToastrService,
    private readonly _store: Store,
    private readonly _translateSvc: TranslateService
  ) {
    this._store
      .select(selectLanguage)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: string) => (this.selectedLanguage = res),
        error: () => {
          this.selectedLanguage = AVAILABLE_LANGUAGES.ES;
        },
      });
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          const body = event.body as
            | { data?: { warning?: unknown; warningStatusCode?: number; warningMessage?: unknown } }
            | null;

          if (body?.data?.warning && body?.data?.warningStatusCode === 422) {
            const message = this.getWarningMessage(body.data.warningMessage);
            this._toastrSvc.warning(message, '', {
              progressBar: true,
              positionClass: 'custom-toastr-top',
              timeOut: 5000,
            });
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (request.url.includes('/get-user-token-app')) {
          return throwError(() => error);
        }

        const backendCode = error?.error?.code || error?.error?.data?.code;
        if (backendCode === 'REPEATED_VOUCHER_CONFIRMATION_REQUIRED' || backendCode === 'ATC_CONCURRENT_CONFLICT') {
          return throwError(() => error);
        }

        const incidentId = error?.error?.incidentId;

        this._toastrSvc.clear();
        setTimeout(() => {
          let errorMessage = this.getErrorMessage(error);

          this._toastrSvc.error(errorMessage, 'Error', {
            progressBar: true,
            positionClass: 'custom-toastr-top',
            timeOut: incidentId ? 15000 : 8000,
            closeButton: true,
          });
        }, 50);
        return throwError(() => error);
      })
    );
  }

  private getWarningMessage(message: unknown): string {
    try {
      if (typeof message === 'object' && message !== null) {
        const localized = message as Record<string, string | undefined>;
        return (
          localized[this.selectedLanguage] ||
          localized[AVAILABLE_LANGUAGES.ES] ||
          this._translateSvc.instant('common.warning')
        );
      }
      return typeof message === 'string'
        ? message
        : this._translateSvc.instant('common.warning');
    } catch {
      return this._translateSvc.instant('common.warning');
    }
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.status === HTTP_STATUS.NETWORK_ERROR) {
      return 'No se pudo conectar con el servidor. Verifique su conexión a internet.';
    }

    const statusFallbacks: Record<number, string> = {
      [HTTP_STATUS.BAD_REQUEST]: this._translateSvc.instant('common.badRequest'),
      [HTTP_STATUS.UNAUTHORIZED]: this._translateSvc.instant('common.unauthorized'),
      [HTTP_STATUS.FORBIDDEN]: this._translateSvc.instant('common.forbidden'),
      [HTTP_STATUS.NOT_FOUND]: this._translateSvc.instant('common.notFound'),
      [HTTP_STATUS.CONFLICT]: this._translateSvc.instant('common.conflict'),
      [HTTP_STATUS.UNPROCESSABLE_ENTITY]: this._translateSvc.instant('common.unprocessableEntity'),
      [HTTP_STATUS.INTERNAL_SERVER_ERROR]: this._translateSvc.instant('common.internalServerError'),
      [HTTP_STATUS.SERVICE_UNAVAILABLE]: this._translateSvc.instant('common.serviceUnavailable'),
    };

    const fallback =
      statusFallbacks[error.status] ||
      this._translateSvc.instant(this.messageError);

    return extractErrorMessage(error, this.selectedLanguage, fallback);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}

export const errorInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
];
